/**
 * MFE-Auth Route Configuration
 *
 * Exports route objects (not JSX) for the auth MFE.
 * Guards and redirects are handled in loaders.
 *
 * Used by Shell to compose the global router via createBrowserRouter.
 */

import React from 'react'
import type { RouteObject } from 'react-router-dom'
import { redirect } from 'react-router-dom'
import { AuthStateManager, PreferencesManager } from '@innolab-trader/mfe-shared'
import { StorageEngine } from '@innolab-trader/storage'

// Lazy-load App layout
const AuthApp = React.lazy(() => import('./App'))

// Lazy-load route components
const WelcomeRoute = React.lazy(() => import('./routes/welcome').then(m => ({ default: m.WelcomeRoute })))
const RegisterRoute = React.lazy(() => import('./routes/register').then(m => ({ default: m.RegisterRoute })))
const RecoverRoute = React.lazy(() => import('./routes/recover').then(m => ({ default: m.RecoverRoute })))
const TwoFactorRoute = React.lazy(() => import('./routes/twoFactor').then(m => ({ default: m.TwoFactorRoute })))
const SignupWithEmailRoute = React.lazy(() => import('./routes/signupWithEmail').then(m => ({ default: m.SignupWithEmailRoute })))
const ConfirmRoute = React.lazy(() => import('./routes/confirm').then(m => ({ default: m.ConfirmRoute })))
const ChangePwdRoute = React.lazy(() => import('./routes/changePwd').then(m => ({ default: m.ChangePwdRoute })))
const CurrentSessionsRoute = React.lazy(() => import('./routes/currentSessions').then(m => ({ default: m.CurrentSessionsRoute })))
const DeviceStatusRoute = React.lazy(() => import('./routes/deviceStatus').then(m => ({ default: m.DeviceStatusRoute })))
const TradingModeRoute = React.lazy(() => import('./routes/tradingMode').then(m => ({ default: m.TradingModeRoute })))
const SignupWithSocialRoute = React.lazy(() => import('./routes/signupWithSocial').then(m => ({ default: m.SignupWithSocialRoute })))
const OAuthCallbackRoute = React.lazy(() => import('./routes/oauthCallback').then(m => ({ default: m.OAuthCallbackRoute })))

const CheckEmailPage = React.lazy(() => import('./pages/CheckEmailPage'))
const ClockWarningPage = React.lazy(() => import('./pages/ClockWarningPage'))
const HelpPage = React.lazy(() => import('./pages/HelpPage'))
const RejectedPage = React.lazy(() => import('./pages/RejectedPage'))
const SocialChoicePage = React.lazy(() => import('./pages/SocialChoicePage'))
const TermsPage = React.lazy(() => import('./pages/TermsPage'))
const UnverifiedPage = React.lazy(() => import('./pages/UnverifiedPage'))
const WelcomeLivePage = React.lazy(() => import('./pages/WelcomeLivePage'))
const ServiceStatusPage = React.lazy(() => import('./pages/ServiceStatusPage'))

/**
 * Loader for routes that should redirect if auth is complete
 * (authenticated + trading environment selected)
 */
async function authCompleteRedirectLoader() {
  const auth = await AuthStateManager.load()
  const isAuthenticated = auth ? AuthStateManager.isAuthValid(auth) : false

  if (isAuthenticated) {
    const sessionPrefs = await PreferencesManager.loadSessionPrefs(StorageEngine.session())
    const hasTradingEnv = !!sessionPrefs.tradingEnvironment

    if (hasTradingEnv) {
      return redirect('/workspace')
    }
  }

  return null
}

/**
 * Loader for /welcome: resets session preferences (trading environment)
 */
async function welcomeLoader() {
  try {
    await PreferencesManager.resetSessionPrefs(StorageEngine.session())
  } catch { /* session reset errors are non-critical */ }
  return null
}

/**
 * Loader for trading-mode page (requires auth, redirects if trading env already selected)
 */
async function tradingModeLoader() {
  const auth = await AuthStateManager.load()
  const isAuthenticated = auth ? AuthStateManager.isAuthValid(auth) : false

  if (!isAuthenticated) {
    return redirect('/welcome')
  }

  // Check for closed account redirect
  // Fetch user accounts to check for closed/restricted status
  if (auth) {
    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 8000)
      const accountsRes = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://demo.tradovateapi.com/v1'}/account/list`,
        {
          headers: {
            Authorization: `Bearer ${auth.accessToken}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        }
      )
      clearTimeout(timeout)

    if (accountsRes.ok) {
      const accounts = await accountsRes.json() as Array<{
        accountType?: number;
        closed?: boolean;
        restricted?: boolean;
      }>

      const demoAccounts = accounts.filter((a: { accountType?: number }) => a.accountType === 1)
      const closedAccounts = accounts.filter((a: { closed?: boolean }) => a.closed)
      const hasRestricted = accounts.some((a: { restricted?: boolean }) => a.restricted)
      const fullyClosed = accounts.length > 0 && accounts.every((a: { closed?: boolean }) => a.closed)

      // Redirect to closed settings if user has only closed/restricted accounts and no demo
      if (fullyClosed && demoAccounts.length === 0 && (hasRestricted || closedAccounts.length > 0)) {
        return redirect('/closed-settings')
      }
    }
    } catch (error) {
      // Non-fatal (timeout/abort or network error): log and continue to trading mode page
      console.warn('Failed to check account status:', error)
    }
  }

  const sessionPrefs = await PreferencesManager.loadSessionPrefs(StorageEngine.session())
  const hasTradingEnv = !!sessionPrefs.tradingEnvironment

  if (hasTradingEnv) {
    return redirect('/workspace')
  }

  return null
}

export function getAuthRoutes(): RouteObject[] {
  return [
    {
      // Layout route - wraps all auth routes with App (providers + AuthLayout)
      element: <AuthApp />,
      children: [
        // Primary authentication routes
        {
          path: '/welcome',
          element: <WelcomeRoute />,
          loader: async () => { await welcomeLoader(); return await authCompleteRedirectLoader(); },
        },
        {
          path: '/welcome-live',
          element: <WelcomeLivePage />,
          loader: authCompleteRedirectLoader,
        },

        // Registration flow
        {
          path: '/register',
          element: <RegisterRoute />,
          loader: authCompleteRedirectLoader,
        },
        {
          path: '/signupWithEmail',
          element: <SignupWithEmailRoute />,
          loader: authCompleteRedirectLoader,
        },
        {
          path: '/check-email',
          element: <CheckEmailPage />,
        },
        {
          path: '/confirm',
          element: <ConfirmRoute />,
        },

        // Two-factor authentication
        {
          path: '/2fa-:method',
          element: <TwoFactorRoute />,
        },

        // Password recovery
        {
          path: '/recover',
          element: <RecoverRoute />,
          loader: authCompleteRedirectLoader,
        },
        {
          path: '/changepwd',
          element: <ChangePwdRoute />,
        },

        // Device & session management (auth required)
        {
          path: '/device-status',
          element: <DeviceStatusRoute />,
        },
        {
          path: '/current-sessions',
          element: <CurrentSessionsRoute />,
        },

        // Trading mode selection (auth required, no trading env yet)
        {
          path: '/trading-mode',
          element: <TradingModeRoute />,
          loader: tradingModeLoader,
        },

        // OAuth flow
        {
          path: '/social-choice',
          element: <SocialChoicePage />,
          loader: authCompleteRedirectLoader,
        },
        {
          path: '/signupWithSocial',
          element: <SignupWithSocialRoute />,
          loader: authCompleteRedirectLoader,
        },
        {
          path: '/auth/callback/:provider',
          element: <OAuthCallbackRoute />,
        },

        // Status pages
        {
          path: '/unverified',
          element: <UnverifiedPage />,
        },
        {
          path: '/rejected',
          element: <RejectedPage />,
        },
        {
          path: '/clock-warning',
          element: <ClockWarningPage />,
        },

        // Info pages
        {
          path: '/terms',
          element: <TermsPage />,
        },
        {
          path: '/help',
          element: <HelpPage />,
        },

        // Service Status (no redirect)
        {
          path: '/service-status',
          element: <ServiceStatusPage />,
        },

        // Catch-all: redirect unrecognized routes to welcome
        {
          path: '*',
          loader: () => redirect('/welcome'),
        },
      ],
    },
  ]
}
